import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  listNewsletters,
  newsletterRecipientCount,
  previewNewsletter,
  sendNewsletter,
  testNewsletter,
  type NewsletterAudience,
  type NewsletterContent,
  type NewsletterSend,
  type NewsletterTestResult,
} from '../lib/admin';

const LOCALES = ['en', 'de', 'fr', 'it'] as const;
const AUDIENCES: { value: NewsletterAudience; label: string }[] = [
  { value: 'both', label: 'Waitlist + users' },
  { value: 'waitlist', label: 'Waitlist only' },
  { value: 'users', label: 'Registered users only' },
];

export function AdminNewsletter() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();

  // Composed content
  const [subject, setSubject] = useState('');
  const [eyebrow, setEyebrow] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [footerNote, setFooterNote] = useState('');
  const [previewLocale, setPreviewLocale] = useState<string>('en');

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  // Test send
  const [testEmails, setTestEmails] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<NewsletterTestResult | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);

  // Real send
  const [audience, setAudience] = useState<NewsletterAudience>('both');
  const [sendLocales, setSendLocales] = useState<string[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  const [sendErr, setSendErr] = useState<string | null>(null);

  // History
  const [sends, setSends] = useState<NewsletterSend[]>([]);

  const content: NewsletterContent = useMemo(
    () => ({
      subject,
      eyebrow: eyebrow || undefined,
      title,
      body,
      buttonLabel: buttonLabel || undefined,
      buttonUrl: buttonUrl || undefined,
      footerNote: footerNote || undefined,
    }),
    [subject, eyebrow, title, body, buttonLabel, buttonUrl, footerNote],
  );

  const ready = subject.trim() && title.trim() && body.trim();

  // Live preview — debounced server render so the admin sees the real email.
  useEffect(() => {
    if (!accessToken || !ready) {
      setPreviewHtml('');
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { html } = await previewNewsletter(accessToken, content, previewLocale);
        setPreviewHtml(html);
        setPreviewErr(null);
      } catch (e: any) {
        setPreviewErr(e?.message || `${e}`);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [accessToken, content, previewLocale, ready]);

  // Recipient count for the chosen audience + locale filter.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setCount(null);
    newsletterRecipientCount(accessToken, audience, sendLocales)
      .then((n) => !cancelled && setCount(n))
      .catch(() => !cancelled && setCount(null));
    return () => {
      cancelled = true;
    };
  }, [accessToken, audience, sendLocales]);

  async function loadHistory() {
    if (!accessToken) return;
    try {
      setSends(await listNewsletters(accessToken));
    } catch {
      /* non-fatal */
    }
  }
  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  function toggleLocale(l: string) {
    setSendLocales((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));
  }

  async function runTest() {
    if (!accessToken || !ready) return;
    const emails = testEmails
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      setTestErr('Add at least one test email.');
      return;
    }
    setTestBusy(true);
    setTestErr(null);
    setTestResult(null);
    try {
      setTestResult(await testNewsletter(accessToken, content, previewLocale, emails));
    } catch (e: any) {
      setTestErr(e?.message || `${e}`);
    } finally {
      setTestBusy(false);
    }
  }

  async function runSend() {
    if (!accessToken || !ready) return;
    const label = AUDIENCES.find((a) => a.value === audience)?.label ?? audience;
    const who = count == null ? 'the selected audience' : `${count} recipient${count === 1 ? '' : 's'}`;
    if (!confirm(`Send "${subject}" to ${who} (${label})?\n\nThis goes out for real. Send a test first if you haven't.`)) {
      return;
    }
    setSendBusy(true);
    setSendErr(null);
    setSendMsg(null);
    try {
      const res = await sendNewsletter(accessToken, content, audience, sendLocales, previewLocale);
      const failedNote = res.failed > 0 ? `, ${res.failed} failed` : '';
      setSendMsg(`Sent to ${res.sent} of ${res.recipients} recipient${res.recipients === 1 ? '' : 's'}${failedNote}.`);
      loadHistory();
    } catch (e: any) {
      setSendErr(e?.message || `${e}`);
    } finally {
      setSendBusy(false);
    }
  }

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Newsletter</h1>
          </header>

          <p class="admin-lede">
            Compose a clean, branded email and watch the live preview update as you type.
            Send a test to yourself first, then blast the waitlist and/or registered users.
            Every email carries a one-click unsubscribe link; opted-out addresses are skipped.
          </p>

          <div class="nl-composer">
            {/* ── Compose ── */}
            <div class="nl-form">
              <label class="nl-field">
                <span>Subject line</span>
                <input
                  type="text"
                  value={subject}
                  maxLength={255}
                  placeholder="The BeerCup app is live 🍺"
                  onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
                />
              </label>

              <label class="nl-field">
                <span>Eyebrow <em>(optional, small uppercase line above the title)</em></span>
                <input
                  type="text"
                  value={eyebrow}
                  maxLength={120}
                  placeholder="BeerCup 2026"
                  onInput={(e) => setEyebrow((e.target as HTMLInputElement).value)}
                />
              </label>

              <label class="nl-field">
                <span>Title</span>
                <input
                  type="text"
                  value={title}
                  maxLength={255}
                  placeholder="It's go time."
                  onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
                />
              </label>

              <label class="nl-field">
                <span>Body <em>(blank line = new paragraph)</em></span>
                <textarea
                  rows={9}
                  value={body}
                  placeholder={'The wait is over — predict every match, climb the leaderboard, and win real beer at venues across Switzerland.\n\nTap below to download the app and make your first picks.'}
                  onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
                />
              </label>

              <div class="nl-field-row">
                <label class="nl-field">
                  <span>Button label <em>(optional)</em></span>
                  <input
                    type="text"
                    value={buttonLabel}
                    maxLength={80}
                    placeholder="Download the app"
                    onInput={(e) => setButtonLabel((e.target as HTMLInputElement).value)}
                  />
                </label>
                <label class="nl-field">
                  <span>Button link</span>
                  <input
                    type="url"
                    value={buttonUrl}
                    placeholder="https://beercup.ch"
                    onInput={(e) => setButtonUrl((e.target as HTMLInputElement).value)}
                  />
                </label>
              </div>
              <p class="nl-hint">The button only shows when both label and link are filled in.</p>

              <label class="nl-field">
                <span>Footer note <em>(optional, above the unsubscribe line)</em></span>
                <input
                  type="text"
                  value={footerNote}
                  maxLength={500}
                  placeholder="Cheers, the BeerCup team"
                  onInput={(e) => setFooterNote((e.target as HTMLInputElement).value)}
                />
              </label>
            </div>

            {/* ── Live preview ── */}
            <div class="nl-preview">
              <div class="nl-preview-head">
                <span class="admin-mono">Live preview</span>
                <select
                  class="admin-filter-select"
                  value={previewLocale}
                  onChange={(e) => setPreviewLocale((e.target as HTMLSelectElement).value)}
                  title="Language of the footer + unsubscribe link. Set it to match the language you wrote the email in — it applies to the test and the real send too."
                >
                  {LOCALES.map((l) => (
                    <option key={l} value={l}>Footer: {l.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              {previewErr && <p class="admin-error">{previewErr}</p>}
              {ready ? (
                <iframe class="nl-preview-frame" title="preview" srcDoc={previewHtml} />
              ) : (
                <div class="nl-preview-empty">Fill in subject, title and body to see the preview.</div>
              )}
            </div>
          </div>

          {/* ── Test send ── */}
          <section class="nl-section">
            <h2>Send a test</h2>
            <p class="nl-hint">
              Comma- or space-separated. Subject is prefixed with <span class="admin-mono">[TEST]</span>.
              Max 20 addresses.
            </p>
            <div class="nl-send-row">
              <input
                class="admin-filter-input"
                type="text"
                value={testEmails}
                placeholder="you@example.com, teammate@example.com"
                onInput={(e) => setTestEmails((e.target as HTMLInputElement).value)}
              />
              <button class="admin-primary" disabled={testBusy || !ready} onClick={runTest}>
                {testBusy ? 'Sending…' : 'Send test'}
              </button>
            </div>
            {testErr && <p class="admin-error">{testErr}</p>}
            {testResult && (
              <p class={testResult.failed > 0 ? 'admin-error' : 'admin-saved'}>
                Sent {testResult.sent}, failed {testResult.failed}.
                {testResult.errors && testResult.errors.length > 0 && ` — ${testResult.errors.join('; ')}`}
              </p>
            )}
          </section>

          {/* ── Real send ── */}
          <section class="nl-section">
            <h2>Send to audience</h2>
            <div class="nl-send-controls">
              <label class="nl-field" style={{ maxWidth: 260 }}>
                <span>Audience</span>
                <select
                  class="admin-filter-select"
                  value={audience}
                  onChange={(e) => setAudience((e.target as HTMLSelectElement).value as NewsletterAudience)}
                >
                  {AUDIENCES.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </label>

              <div class="nl-field">
                <span>Locales <em>(none = all)</em></span>
                <div class="nl-locale-chips">
                  {LOCALES.map((l) => (
                    <button
                      type="button"
                      key={l}
                      class={`admin-chip ${sendLocales.includes(l) ? 'on' : ''}`}
                      onClick={() => toggleLocale(l)}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div class="nl-send-row" style={{ marginTop: 14 }}>
              <span class="admin-mono nl-count">
                {count == null ? 'Counting recipients…' : `${count} recipient${count === 1 ? '' : 's'}`}
              </span>
              <button
                class="admin-primary"
                disabled={sendBusy || !ready || count === 0}
                onClick={runSend}
              >
                {sendBusy ? 'Sending…' : count == null ? 'Send' : `Send to ${count}`}
              </button>
            </div>
            {sendErr && <p class="admin-error">{sendErr}</p>}
            {sendMsg && <p class="admin-saved">{sendMsg}</p>}
          </section>

          {/* ── History ── */}
          {sends.length > 0 && (
            <section class="nl-section">
              <h2>Recent sends</h2>
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>Sent</th>
                    <th>Subject</th>
                    <th>Audience</th>
                    <th>Locales</th>
                    <th>Recipients</th>
                    <th>Delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {sends.map((s) => (
                    <tr key={s.id}>
                      <td class="admin-mono">{fmtDate(s.createdAt)}</td>
                      <td>{s.subject}</td>
                      <td class="admin-mono">{s.audience}</td>
                      <td class="admin-mono">{s.locales || 'all'}</td>
                      <td class="admin-mono">{s.recipients}</td>
                      <td class="admin-mono">
                        {s.sent}
                        {s.failed > 0 && <span class="admin-error"> · {s.failed} failed</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button class="admin-link" style={{ marginTop: 10 }} onClick={loadHistory}>
                ↻ Refresh
              </button>
            </section>
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function fmtDate(iso: string): string {
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}
